// Modern-style building blocks for the Lake document detail sheet.
// Mirrors the case `DocumentDetailOverlay` design language: --md-* tokens,
// inline styles, mono accents for numeric values, and consistent radii.

import type React from 'react'

// ─── DetailCard ──────────────────────────────────────────────────────────────

export function DetailCard({
  children,
  padding = 18,
  style,
}: {
  children: React.ReactNode
  padding?: number | string
  style?: React.CSSProperties
}) {
  return (
    <div
      style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        padding,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

// ─── SectionHeading ──────────────────────────────────────────────────────────

export function SectionHeading({
  icon,
  label,
  count,
  trailing,
}: {
  icon?: React.ReactNode
  label: React.ReactNode
  count?: number | string
  trailing?: React.ReactNode
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 18,
      }}
    >
      {icon && (
        <span
          aria-hidden
          style={{
            display: 'inline-flex',
            color: 'var(--muted-foreground)',
            flexShrink: 0,
          }}
        >
          {icon}
        </span>
      )}
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--foreground)',
          letterSpacing: -0.05,
        }}
      >
        {label}
      </span>
      {count != null && (
        <span
          style={{
            fontSize: 11,
            color: 'var(--muted-foreground)',
            fontFamily: 'var(--font-mono)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {count}
        </span>
      )}
      {trailing && <span style={{ marginLeft: 'auto' }}>{trailing}</span>}
    </div>
  )
}

// ─── PageTitle (uppercase eyebrow used above each tab body) ──────────────────

export function PageTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        margin: 0,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 1.4,
        textTransform: 'uppercase',
        color: 'var(--muted-foreground)',
      }}
    >
      {children}
    </h2>
  )
}

// ─── MetaField ───────────────────────────────────────────────────────────────

export function MetaField({
  label,
  value,
  sub,
  mono = false,
}: {
  label: React.ReactNode
  value: React.ReactNode
  sub?: React.ReactNode
  mono?: boolean
}) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 10,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          color: 'var(--muted-foreground)',
          fontWeight: 600,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--foreground)',
          fontFamily: mono ? 'var(--font-mono)' : 'inherit',
          wordBreak: mono ? 'break-all' : 'break-word',
          lineHeight: 1.45,
        }}
      >
        {value}
      </div>
      {sub != null && sub !== '' && (
        <div
          style={{
            fontSize: 11,
            color: 'var(--muted-foreground)',
            fontFamily: 'var(--font-mono)',
            marginTop: 3,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  )
}

// ─── OutlineBadge ────────────────────────────────────────────────────────────

export function OutlineBadge({
  children,
  tone = 'neutral',
  mono = false,
  style,
}: {
  children: React.ReactNode
  tone?: 'neutral' | 'accent' | 'success' | 'error' | 'warning' | 'info' | 'mute'
  mono?: boolean
  style?: React.CSSProperties
}) {
  const palette: Record<
    NonNullable<Parameters<typeof OutlineBadge>[0]['tone']>,
    { bg: string; color: string; border: string }
  > = {
    neutral: {
      bg: 'var(--card)',
      color: 'var(--foreground)',
      border: 'var(--border)',
    },
    accent: {
      bg: 'var(--primary-surface)',
      color: 'var(--primary)',
      border: 'var(--primary)',
    },
    success: {
      bg: 'var(--success-surface)',
      color: 'var(--success)',
      border: 'var(--success)',
    },
    error: {
      bg: 'var(--destructive-surface)',
      color: 'var(--destructive)',
      border: 'var(--destructive)',
    },
    warning: {
      bg: 'rgba(199, 127, 42, 0.12)',
      color: 'var(--primary)',
      border: 'rgba(199, 127, 42, 0.55)',
    },
    info: {
      bg: 'rgba(80, 144, 224, 0.14)',
      color: '#5b9be0',
      border: 'rgba(80, 144, 224, 0.45)',
    },
    mute: {
      bg: 'transparent',
      color: 'var(--muted-foreground)',
      border: 'var(--border)',
    },
  }
  const c = palette[tone]
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 9px',
        borderRadius: '9999px',
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
        fontSize: 11,
        fontWeight: 500,
        fontFamily: mono ? 'var(--font-mono)' : 'inherit',
        letterSpacing: mono ? 0.6 : 0,
        textTransform: mono ? 'uppercase' : 'none',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children}
    </span>
  )
}

// ─── StatusPill (specifically for indexing/processing status) ────────────────

export function StatusPill({ status }: { status: string }) {
  const tone = resolveStatusTone(status)
  return (
    <OutlineBadge tone={tone} mono>
      {status}
    </OutlineBadge>
  )
}

function resolveStatusTone(status: string): 'success' | 'info' | 'neutral' | 'error' | 'warning' {
  switch (status) {
    case 'processed':
    case 'indexed':
      return 'success'
    case 'processing':
    case 'analyzing':
      return 'info'
    case 'failed':
      return 'error'
    case 'skipped':
      return 'warning'
    default:
      return 'neutral'
  }
}

// ─── FileIcon ────────────────────────────────────────────────────────────────

export function FileIcon({ kind }: { kind: string | null }) {
  const upper = (kind ?? 'FILE').toUpperCase()
  const palette: Record<string, { bg: string; color: string; border: string }> = {
    PDF: { bg: 'rgba(242, 92, 92, 0.16)', color: '#f25c5c', border: 'rgba(242, 92, 92, 0.45)' },
    PNG: { bg: 'rgba(179, 156, 255, 0.18)', color: '#b39cff', border: 'rgba(179, 156, 255, 0.45)' },
    JPG: { bg: 'rgba(179, 156, 255, 0.18)', color: '#b39cff', border: 'rgba(179, 156, 255, 0.45)' },
    JPEG: {
      bg: 'rgba(179, 156, 255, 0.18)',
      color: '#b39cff',
      border: 'rgba(179, 156, 255, 0.45)',
    },
    CSV: { bg: 'rgba(139, 227, 139, 0.16)', color: '#4a8e5a', border: 'rgba(139, 227, 139, 0.45)' },
    XLSX: {
      bg: 'rgba(139, 227, 139, 0.16)',
      color: '#4a8e5a',
      border: 'rgba(139, 227, 139, 0.45)',
    },
    XLS: { bg: 'rgba(139, 227, 139, 0.16)', color: '#4a8e5a', border: 'rgba(139, 227, 139, 0.45)' },
    TXT: {
      bg: 'var(--card)',
      color: 'var(--foreground)',
      border: 'var(--border)',
    },
  }
  const tone = palette[upper] ?? palette.TXT
  return (
    <div
      style={{
        width: 32,
        height: 40,
        flexShrink: 0,
        borderRadius: '8px',
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--font-mono)',
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: 0.4,
      }}
    >
      {upper}
    </div>
  )
}

// ─── KeyValueRow (used in compact label-value rows) ──────────────────────────

export function KeyValueRow({
  label,
  value,
  mono = false,
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
      <span
        style={{
          fontSize: 11,
          color: 'var(--muted-foreground)',
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 12,
          color: 'var(--foreground)',
          fontFamily: mono ? 'var(--font-mono)' : 'inherit',
          wordBreak: 'break-all',
          minWidth: 0,
        }}
      >
        {value}
      </span>
    </div>
  )
}

// ─── BulletList (clean indented list with custom markers) ───────────────────

export function BulletList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul
      style={{
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
      }}
    >
      {items.map((item, i) => (
        <li
          key={i}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            paddingLeft: 6,
            fontSize: 12.5,
            lineHeight: 1.55,
            color: 'var(--foreground)',
          }}
        >
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              width: 3,
              height: 3,
              marginTop: 8,
              borderRadius: '50%',
              background: 'var(--muted-foreground)',
              flexShrink: 0,
            }}
          />
          <span style={{ minWidth: 0, flex: 1 }}>{item}</span>
        </li>
      ))}
    </ul>
  )
}

// ─── EntityChipTone (shared palette for small entity-type badges) ───────────

export function EntityChipTone(type: string): { bg: string; color: string; border: string } {
  const palette: Record<string, { bg: string; color: string; border: string }> = {
    Person: {
      bg: 'rgba(111, 227, 212, 0.14)',
      color: '#3a9b8a',
      border: 'rgba(111, 227, 212, 0.45)',
    },
    Company: {
      bg: 'rgba(255, 176, 82, 0.14)',
      color: '#b06d20',
      border: 'rgba(255, 176, 82, 0.45)',
    },
    Date: {
      bg: 'rgba(139, 227, 139, 0.14)',
      color: '#4a8e5a',
      border: 'rgba(139, 227, 139, 0.45)',
    },
    Role: {
      bg: 'rgba(179, 156, 255, 0.14)',
      color: '#7d6bd1',
      border: 'rgba(179, 156, 255, 0.45)',
    },
    Document: {
      bg: 'var(--card)',
      color: 'var(--foreground)',
      border: 'var(--border)',
    },
    Location: {
      bg: 'rgba(139, 227, 139, 0.14)',
      color: '#4a8e5a',
      border: 'rgba(139, 227, 139, 0.45)',
    },
  }
  return (
    palette[type] ?? {
      bg: 'rgba(80, 144, 224, 0.12)',
      color: '#3a7bb8',
      border: 'rgba(80, 144, 224, 0.4)',
    }
  )
}
